import { DateUtils } from './date-utils';
import * as moment from 'moment';
import { SubmissionStatusIdEnum } from '../models/enum/shared/submission-status-id.enum';

export class OdataUtils {
  /**
   * Escapes a value for use inside an OData string literal.
   * Single quotes must be doubled ('') per the OData spec, otherwise
   * search terms containing an apostrophe break the $filter expression.
   */
  static escapeFilterValue(value: string | number | null | undefined): string {
    return String(value ?? '').replace(/'/g, `''`);
  }

  /**
   * @deprecated Use a TableHeaderInput instead.
   */
  static constructDateSearchFilterQuery(date: string, dateFieldName: string): string {
    const dateAdjustedForBackend = DateUtils.swapDayAndMonth(date);
    const startDate = moment.utc(dateAdjustedForBackend, 'MM/DD/YYYY').startOf('day');
    const endDate = startDate.clone().endOf('day');
    return `${dateFieldName} ge cast(${startDate.toISOString(
      true
    )}, Edm.DateTimeOffset) and ${dateFieldName} le cast(${endDate.toISOString(true)}, Edm.DateTimeOffset)`;
  }

  static ConstructTableDateSearchFilterQuery(date: string, dateFieldName: string): string {
    const startDate = moment.utc(date).startOf('day');
    const endDate = startDate.clone().endOf('day');
    return `${dateFieldName} ge cast(${startDate.toISOString(
      true
    )}, Edm.DateTimeOffset) and ${dateFieldName} le cast(${endDate.toISOString(true)}, Edm.DateTimeOffset)`;
  }

  /**
   * For backend fields typed `DateOnly` (Edm.Date, no time component). A single-value equality
   * check - there's no time-of-day to bound, so this is a plain `eq` rather than a ge/le range
   * built from start-of-day/end-of-day boundaries. `date` is expected to already be a clean
   * `yyyy-MM-dd` string.
   */
  static constructDateOnlySearchFilterQuery(date: string, dateFieldName: string): string {
    return `${dateFieldName} eq cast(${date}, Edm.Date)`;
  }

  /**
   * For backend fields typed `DateOnly` (Edm.Date, no time component). A range filter between two
   * already-date-only values - no start-of-day/end-of-day boundary math needed, since Edm.Date has
   * no time to bound; ge/le on the plain date literal is already inclusive of the whole day.
   */
  static constructDateOnlyRangeFilterQuery(startDate: string, endDate: string, dateFieldName: string): string {
    return `${dateFieldName} ge cast(${startDate}, Edm.Date) and ${dateFieldName} le cast(${endDate}, Edm.Date)`;
  }

  static activeSubmissionFilterString(): string {
    return `(statusID eq ${SubmissionStatusIdEnum.Pending} or statusID eq ${SubmissionStatusIdEnum.Submitted} or statusID eq ${SubmissionStatusIdEnum.InReview})`;
  }

  static odataFilterQueryFromFormGroup(val: any[]): string {
    return val
      .map(([key, v]) => {
        if (typeof v === 'string') {
          return `contains(${key}, '${this.escapeFilterValue(v)}')`;
        }
        return `${key} eq ${this.formatOdataFilterParam(v)}`;
      })
      .join(' and ');
  }

  static odataNumberFilterQueryFromFormGroup(val: any[]): string {
    return val.map(([key, v]) => `${key} eq ${v}`).join(' and ');
  }

  static odataDateFilterQueryFromFormGroup(val: any[]): string {
    return val
      .map(([dateFieldName, date]) => {
        const startDate = moment(date);
        const endDate = startDate.clone().endOf('day');
        return `${dateFieldName} ge cast(${startDate.toISOString(
          true
        )}, Edm.DateTimeOffset) and ${dateFieldName} le cast(${endDate.toISOString(true)}, Edm.DateTimeOffset)`;
      })
      .join(' and ');
  }

  static formatOdataFilterParam(val: unknown): string {
    if (typeof val === 'string') {
      return `'${this.escapeFilterValue(val)}'`;
    }
    return String(val);
  }
}
